/**
 * Split a list of rows into named groups, by predicate.
 *
 * `MultiList` did this in code, as `filterItems(data, docId, lineId)`: rows this
 * document already reserved against this line went to one list and the rest to
 * another. Three of the app's configs declared `filter: 'reservedQuantity'`
 * beside it, which nothing read — someone had already tried to say this in the
 * model and the consumer grew its own version instead (33.2 §1.9).
 *
 * So the rule moves here, and the language is the one the app already has:
 * `field` / `operator` / `value` / `valueFrom`, composed with `all` and `any`,
 * quantified over a child array with `some` and `none`.
 *
 *   partitions: [
 *     { id: 'reserved',  condition: { … }, sum: { … } },
 *     { id: 'available', condition: { … } },
 *   ]
 *
 * ## Order decides
 *
 * The first partition whose condition holds takes the row, because the code this
 * replaces was an if/else and a location can satisfy both: reserved by this line
 * AND still holding stock for someone else. A row that matches nothing belongs
 * to no group and is dropped — also as before, which is how a location with
 * nothing available stays out of the list.
 *
 * ## `sum` is a separate question
 *
 * A reserved row shows how much of it is this line's, which is a total over the
 * matching children — not something a predicate can answer. `sum` declares it:
 *
 *   sum: { of: 'reservations', condition: { … }, field: 'quantity', as: 'held' }
 *
 * Its `condition` repeats the quantifier in the partition's own, deliberately.
 * "Does this row belong in this group" and "how much of it is mine" are two
 * questions that happen to share a predicate today; binding the second to the
 * internals of the first would make a change to either one break the other
 * silently. An omitted condition totals every child.
 *
 * It is spelled `condition` rather than `where` so that `lint:conditions`, which
 * checks every `condition` it walks past, checks this one too. A predicate the
 * checker cannot see is how `body.filter` stayed wrong for years.
 *
 * Rows are never mutated — `sum` returns a copy. The rows come from a cached
 * query, and the code this replaces wrote `item.displayQuantity` straight onto
 * them.
 */

import { evaluateCondition } from './evaluate.js'

/** Total one field over the children a predicate selects, onto a copy of the row. */
function withSum(row, sum, sources) {
    const children = row?.[sum?.of]
    if (!Array.isArray(children)) return row

    const total = children.reduce((acc, child) => {
        if (!evaluateCondition(sum.condition, { ...sources, values: child, parent: row })) return acc
        const n = Number(child?.[sum.field])
        return Number.isFinite(n) ? acc + n : acc
    }, 0)

    return { ...row, [sum.as]: total }
}

/**
 * Group `rows` by the first partition each one satisfies.
 *
 * Every declared id is present in the result, empty if nothing matched, so a
 * caller can destructure without checking. `sources` is the surrounding context
 * — `values` is filled in per row.
 */
export function partitionRows(rows, partitions, sources = {}) {
    const groups = Array.isArray(partitions) ? partitions : []
    const out = {}
    for (const group of groups) out[group.id] = []

    if (!Array.isArray(rows)) return out

    for (const row of rows) {
        const rowSources = { ...sources, values: row }
        const hit = groups.find(group => evaluateCondition(group.condition, rowSources))
        if (!hit) continue
        out[hit.id].push(hit.sum ? withSum(row, hit.sum, rowSources) : row)
    }

    return out
}

export default partitionRows
